import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Link,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertState } from '../../../abelcommon';
import { CenteredTableCell } from '../../../abelcommon/components/CenteredTableCell';
import { CopyOnClickContainer } from '../../../abelcommon/components/CopyOnClickContainer';
import { env } from '../../../api';
import { loadBalancerAPI, MiningAccount } from '../../../api/load-balancer';
import { AddEditAccountModal, AddEditAccountModalType } from './AddEditAccountModal';
import { AddWalletAddressModal } from './AddWalletAddressModal';
import { DeleteAccountModal } from './DeleteAccountModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import { ResetPasswordSuccessModal } from './ResetPasswordSuccessModal';

export const AccountSettingPage = () => {
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<MiningAccount>(null);
  const [deletingAccount, setDeletingAccount] = useState<MiningAccount>(null);
  const [resetPasswordAccount, setResetPasswordAccount] = useState<MiningAccount>(null);
  const [addingWalletAddressAccount, setAddingWalletAddressAccount] = useState<MiningAccount>(null);
  const [alertState, setAlertState] = useState<AlertState>({
    isOpen: false,
    severity: 'success',
    message: '',
  });
  const [refetchKey, setRefetchKey] = useState<string>('');
  const [showResetPasswordSuccessModal, setShowResetPasswordSuccessModal] = useState(false);
  const { t } = useTranslation();

  const {
    data: miningAccounts,
    isLoading: miningAccountsLoading,
    error: miningAccountError,
    refetch: refetchMiningAccounts,
  } = loadBalancerAPI.hookWrapperQuery.useGetMiningAccounts();

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={t('accountSettingPage.accounts')}
            action={
              <Button variant="contained" sx={{ fontSize: '0.8125rem' }} onClick={() => setIsAddAccountModalOpen(true)}>
                {t('accountSettingPage.addAccount')}
              </Button>
            }
          />
          <CardContent sx={{ overflow: 'auto' }}>
            <Table sx={{ overflowWrap: 'break-word' }}>
              <TableHead>
                <TableRow>
                  <CenteredTableCell>{t('accountSettingPage.miningAccounts')}</CenteredTableCell>
                  <CenteredTableCell sx={{ width: '200px' }}>{t('accountSettingPage.description')}</CenteredTableCell>
                  <CenteredTableCell>{t('accountSettingPage.actions')}</CenteredTableCell>
                  <CenteredTableCell sx={{ width: '50px' }}></CenteredTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {miningAccountError && <Alert severity="error">{miningAccountError.message}</Alert>}
                {miningAccountsLoading ? (
                  <TableRow>
                    <CenteredTableCell colSpan={4}>
                      <CircularProgress />
                    </CenteredTableCell>
                  </TableRow>
                ) : !miningAccounts || miningAccounts.length === 0 ? (
                  <TableRow>
                    <CenteredTableCell colSpan={4}>{t('global.noData')}</CenteredTableCell>
                  </TableRow>
                ) : (
                  miningAccounts.map((account) => (
                    <AccountTableRow
                      key={account.id}
                      accountInfo={account}
                      refetchKey={refetchKey}
                      setEditingAccount={setEditingAccount}
                      setDeletingAccount={setDeletingAccount}
                      setResetPasswordAccount={setResetPasswordAccount}
                      setAddingWalletAddressAccount={setAddingWalletAddressAccount}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>
      {isAddAccountModalOpen && (
        <AddEditAccountModal
          type={AddEditAccountModalType.Add}
          onClose={() => setIsAddAccountModalOpen(false)}
          onError={(message) =>
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'error',
            }))
          }
          onSuccess={(message) => {
            refetchMiningAccounts();
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'success',
            }));
          }}
        />
      )}
      {editingAccount != null && (
        <AddEditAccountModal
          miningAccount={editingAccount}
          type={AddEditAccountModalType.Edit}
          onClose={() => setEditingAccount(null)}
          onError={(message) =>
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'error',
            }))
          }
          onSuccess={(message) => {
            refetchMiningAccounts();
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'success',
            }));
          }}
        />
      )}
      {deletingAccount != null && (
        <DeleteAccountModal
          miningAccount={deletingAccount}
          onClose={() => setDeletingAccount(null)}
          onError={(message) =>
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'error',
            }))
          }
          onSuccess={(message) => {
            refetchMiningAccounts();
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'success',
            }));
          }}
        />
      )}
      {resetPasswordAccount != null && (
        <ResetPasswordModal
          miningAccount={resetPasswordAccount}
          onClose={() => setResetPasswordAccount(null)}
          onError={(message) =>
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'error',
            }))
          }
          onSuccess={(message, refetchKey) => {
            setRefetchKey(refetchKey);
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'success',
            }));
            setShowResetPasswordSuccessModal(true);
          }}
        />
      )}
      {addingWalletAddressAccount != null && (
        <AddWalletAddressModal
          miningAccount={addingWalletAddressAccount}
          onClose={() => setAddingWalletAddressAccount(null)}
          onError={(message) =>
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'error',
            }))
          }
          onSuccess={(message) => {
            refetchMiningAccounts();
            setAlertState((prevState) => ({
              ...prevState,
              isOpen: true,
              message,
              severity: 'success',
            }));
          }}
        />
      )}
      {showResetPasswordSuccessModal && (
        <ResetPasswordSuccessModal
          miningAccount={resetPasswordAccount}
          onClose={() => setShowResetPasswordSuccessModal(false)}
        />
      )}
      <Snackbar
        open={alertState.isOpen}
        autoHideDuration={alertState.autoHideDuration === undefined ? 10000 : alertState.autoHideDuration}
        onClose={(e, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setAlertState((prev) => ({ ...prev, isOpen: false }));
        }}
      >
        <Alert
          onClose={() => setAlertState((prev) => ({ ...prev, isOpen: false }))}
          severity={alertState.severity}
          variant="filled"
          sx={{ width: '100%', color: 'white' }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

const AccountTableRow = ({
  accountInfo,
  refetchKey,
  setEditingAccount,
  setDeletingAccount,
  setResetPasswordAccount,
  setAddingWalletAddressAccount,
}: {
  accountInfo: MiningAccount;
  refetchKey?: string;
  setEditingAccount: (account: MiningAccount) => void;
  setDeletingAccount: (account: MiningAccount) => void;
  setResetPasswordAccount: (account: MiningAccount) => void;
  setAddingWalletAddressAccount: (account: MiningAccount) => void;
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);

  const {
    data: miningAccountDetail,
    isLoading: miningAccountLoading,
    error: miningAccountError,
  } = loadBalancerAPI.hookWrapperQuery.useGetMiningAccountById(
    accountInfo.id,
    !accountInfo.id || !open,
    `${accountInfo.walletAddress}${refetchKey ?? ''}`
  );

  const {
    data: longWalletAddress,
    isLoading: longWalletAddressLoading,
    error: longWalletAddressError,
  } = loadBalancerAPI.hookWrapperQuery.useGetLongWalletAddress(
    accountInfo.walletAddress,
    !open || !accountInfo.id || !accountInfo.walletAddress,
    accountInfo.id
  );

  const { details, gpuCommands, cpuCommands } = useMemo(() => {
    if (!miningAccountDetail || !miningAccountDetail.walletAddress)
      return { details: [], gpuCommands: [], cpuCommands: {} };
    const bashScriptCommand = `bash <(curl -sSL ${env.miningScriptHost})`;
    const commandParam = `${miningAccountDetail.username}:${miningAccountDetail.password}`;
    const longAddressValue = longWalletAddress ?? 'N/A';
    const gpu = [
      {
        label: t('accountSettingPage.startMining'),
        value: `${bashScriptCommand} ${t('accountSettingPage.start')} ${commandParam}`,
      },
      { label: t('accountSettingPage.stopMining'), value: `${bashScriptCommand} ${t('accountSettingPage.stop')}` },
      { label: t('accountSettingPage.status'), value: `${bashScriptCommand} ${t('accountSettingPage.statusLowCase')}` },
      { label: t('accountSettingPage.help'), value: `${bashScriptCommand} ${t('accountSettingPage.helpLowCase')}` },
      {
        label: t('accountSettingPage.manual'),
        value: `${bashScriptCommand} ${t('accountSettingPage.manualLowCase')} ${commandParam}`,
      },
    ];

    const cpuScriptCommand = `bash <(curl -sSL ${env.cpuMiningScriptHost})`;
    const cpuLinuxAmd64CommandParam = `amd64 ${miningAccountDetail.username} ${miningAccountDetail.password}`;
    const cpuLinuxAmd64 = [
      {
        label: t('accountSettingPage.startMining'),
        value: `${cpuScriptCommand} ${t('accountSettingPage.start')} ${cpuLinuxAmd64CommandParam}`,
      },
      { label: t('accountSettingPage.stopMining'), value: `${cpuScriptCommand} ${t('accountSettingPage.stop')}` },
      { label: t('accountSettingPage.status'), value: `${cpuScriptCommand} ${t('accountSettingPage.statusLowCase')}` },
    ];

    const cpuLinuxArm64CommandParam = `arm64 ${miningAccountDetail.username} ${miningAccountDetail.password}`;
    const cpuLinuxArm64 = [
      {
        label: t('accountSettingPage.startMining'),
        value: `${cpuScriptCommand} ${t('accountSettingPage.start')} ${cpuLinuxArm64CommandParam}`,
      },
      { label: t('accountSettingPage.stopMining'), value: `${cpuScriptCommand} ${t('accountSettingPage.stop')}` },
      { label: t('accountSettingPage.status'), value: `${cpuScriptCommand} ${t('accountSettingPage.statusLowCase')}` },
    ];

    const cpuMacAmd64 = [
      {
        label: t('accountSettingPage.minerDownload'),
        value: env.macAmd64DownloadURL,
      },
      {
        label: t('accountSettingPage.startMining'),
        value: `./abelminer-cpu -u ${miningAccountDetail.username} -p ${miningAccountDetail.password}`,
      },
    ];

    const cpuMacArm64 = [
      {
        label: t('accountSettingPage.minerDownload'),
        value: env.macArm64DownloadURL,
      },
      {
        label: t('accountSettingPage.startMining'),
        value: `./abelminer-cpu -u ${miningAccountDetail.username} -p ${miningAccountDetail.password}`,
      },
    ];

    const cpuWindows = [
      {
        label: t('accountSettingPage.minerDownload'),
        value: env.windowsDownloadURL,
      },
      {
        label: t('accountSettingPage.startMining'),
        value: `.\\abelminer-cpu.exe -u ${miningAccountDetail.username} -p ${miningAccountDetail.password}`,
      },
    ];

    const commandToJsx = (commands: { label: string; value: string }[]) =>
      commands.map(({ label, value }, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</Typography>
          {label === t('accountSettingPage.minerDownload') ? (
            <Link href={value} target="_blank" rel="noopener noreferrer">
              {value}
            </Link>
          ) : (
            <code>{value}</code>
          )}
          <CopyOnClickContainer copyContent={value}>
            <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
              <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />
            </Box>
          </CopyOnClickContainer>
        </Box>
      ));

    return {
      details: [
        {
          key: t('accountSettingPage.tagName'),
          value: miningAccountDetail.name,
        },
        {
          key: t('accountSettingPage.shortAddress'),
          originalValue: miningAccountDetail.walletAddress,
          value:
            miningAccountDetail.walletAddress.length > 32
              ? `${miningAccountDetail.walletAddress.slice(0, 16)}...${miningAccountDetail.walletAddress.slice(-16)}`
              : miningAccountDetail.walletAddress,
        },
        {
          key: t('accountSettingPage.address'),
          originalValue: longAddressValue,
          value:
            longAddressValue.length > 32
              ? `${longAddressValue.slice(0, 16)}...${longAddressValue.slice(-16)}`
              : longAddressValue,
        },
        {
          key: t('accountSettingPage.username'),
          value: miningAccountDetail.username,
        },
        {
          key: t('accountSettingPage.password'),
          value: miningAccountDetail.password,
        },
      ],
      gpuCommands: commandToJsx(gpu),
      cpuCommands: {
        linuxAmd64: commandToJsx(cpuLinuxAmd64),
        linuxArm64: commandToJsx(cpuLinuxArm64),
        macAmd64: commandToJsx(cpuMacAmd64),
        macArm64: commandToJsx(cpuMacArm64),
        windows: commandToJsx(cpuWindows),
      },
    };
  }, [miningAccountDetail, longWalletAddress, t]);

  const apiError = miningAccountError || longWalletAddressError;

  return (
    <>
      <TableRow>
        <CenteredTableCell>{accountInfo.name}</CenteredTableCell>
        <CenteredTableCell>{accountInfo.description}</CenteredTableCell>
        <CenteredTableCell>
          <Stack sx={{ flexDirection: 'row', gap: 1, justifyContent: 'center' }}>
            <Button size="small" onClick={() => setEditingAccount(accountInfo)} sx={{ color: 'appBlue.contrastText' }}>
              {t('accountSettingPage.edit')}
            </Button>
            <Button size="small" onClick={() => setDeletingAccount(accountInfo)} sx={{ color: 'appBlue.contrastText' }}>
              {t('accountSettingPage.delete')}
            </Button>
            <Button
              size="small"
              onClick={() => setResetPasswordAccount(accountInfo)}
              sx={{ color: 'appBlue.contrastText' }}
            >
              {t('accountSettingPage.resetPassword')}
            </Button>
          </Stack>
        </CenteredTableCell>
        <CenteredTableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </CenteredTableCell>
      </TableRow>
      <TableRow>
        <CenteredTableCell sx={{ py: 0 }} colSpan={4} align="left">
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2 }}>
              {apiError && <Alert severity="error">{apiError.message}</Alert>}
              {miningAccountLoading || longWalletAddressLoading ? (
                <CircularProgress sx={{ mx: 'auto', display: 'block' }} />
              ) : details.length === 0 ? (
                <Button
                  sx={{ fontSize: '0.8125rem', ml: 'auto', display: 'block' }}
                  variant="contained"
                  onClick={() => setAddingWalletAddressAccount(accountInfo)}
                >
                  {t('accountSettingPage.addAddress')}
                </Button>
              ) : (
                <>
                  {details.map(({ key, value, originalValue }) => (
                    <Grid container spacing={2} key={key} sx={{ my: 1, maxWidth: '100%' }}>
                      <Grid item xs={4} lg={2}>
                        <Typography variant="body1">{key}</Typography>
                      </Grid>
                      <Grid item xs={8} lg={10}>
                        <Box>
                          <Typography variant="body1" sx={{ display: 'inline' }}>
                            {value}
                          </Typography>
                          <CopyOnClickContainer copyContent={originalValue ?? `${value}`}>
                            <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                              <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />
                            </Box>
                          </CopyOnClickContainer>
                        </Box>
                      </Grid>
                    </Grid>
                  ))}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                      value={selectedTab}
                      onChange={(e, newValue) => setSelectedTab(newValue)}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      <Tab label={t('accountSettingPage.gpuMining')} />
                      <Tab label={`${t('accountSettingPage.cpuMining')} - Linux (amd64)`} />
                      <Tab label={`${t('accountSettingPage.cpuMining')} - Linux (arm64)`} />
                      <Tab label={`${t('accountSettingPage.cpuMining')} - macOS (amd64)`} />
                      <Tab label={`${t('accountSettingPage.cpuMining')} - macOS (arm64)`} />
                      <Tab label={`${t('accountSettingPage.cpuMining')} - Windows`} />
                    </Tabs>
                  </Box>
                  <Box sx={{ pt: 2 }}>
                    {selectedTab === 0 && gpuCommands}
                    {selectedTab === 1 && cpuCommands.linuxAmd64}
                    {selectedTab === 2 && cpuCommands.linuxArm64}
                    {selectedTab === 3 && cpuCommands.macAmd64}
                    {selectedTab === 4 && cpuCommands.macArm64}
                    {selectedTab === 5 && cpuCommands.windows}
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </CenteredTableCell>
      </TableRow>
    </>
  );
};
